import { toast } from "react-toastify"

import {
    AudioDeviceManager,
    CameraManager,
    InputDevice,
    InputDeviceManager,
} from "modules/Call/sdk"
import { ErrorWithFallback } from "modules/Call/common/utils"
import { SelectedDevicesStorage } from "modules/Call/InputDevices/SelectedDevicesStorage"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"

export type VoximplantInputDeviceChangeListener = () => void
export class VoximplantInputDevice<T extends InputDeviceManager> {
    list: InputDevice[] = []
    error?: Error
    private current?: InputDevice

    constructor(
        private manager: T,
        private storage: SelectedDevicesStorage
    ) {
        this.enabled = this.storage.state
    }

    async reload() {
        await this.loadDevices()
        if (this.error) return
        this.current = this.getDeviceOrDefault(this.storage.deviceId)
        if (!this.current?.id) return
        this.storage.deviceId = this.current.id
    }

    // Single thread only allowed
    async getAccess() {
        const options = {
            audio: this.manager === AudioDeviceManager,
            video: this.manager === CameraManager,
        }

        try {
            const media = await navigator.mediaDevices.getUserMedia(options)
            media.getTracks().forEach((track) => track.stop())
            return true
        } catch (e) {
            handleError(this.manager, e)
            return false
        }
    }

    get selected() {
        return this.current
    }

    select(id: InputDevice["id"]) {
        this.current = this.getDeviceOrDefault(id)
        if (!this.current) return
        this.storage.deviceId = this.current.id
    }

    get enabled() {
        return this.storage.state
    }

    set enabled(on: boolean) {
        this.storage.state = on
    }

    getById(id: InputDevice["id"]) {
        return this.list?.find((item) => item.id === id)
    }

    static onChangeListener(listener: VoximplantInputDeviceChangeListener) {
        navigator.mediaDevices.addEventListener("devicechange", listener)
    }

    static offChangeListener(listener: VoximplantInputDeviceChangeListener) {
        navigator.mediaDevices.removeEventListener("devicechange", listener)
    }

    protected getDeviceOrDefault(id?: InputDevice["id"]) {
        if (id === undefined) return
        const device = this.getById(id)
        if (device) return device
        return this.list?.[0]
    }

    private async loadDevices() {
        this.error = undefined
        this.list = []

        try {
            const list = (await this.manager.get().getInputDevices()).filter(
                (device) => typeof device.id === "number" || device.id.trim() !== ""
            )
            if (!list.length) {
                this.error = new NoDeviceFoundError()
                handleError(this.manager, this.error)
            }
            this.list = list
        } catch (e) {
            this.error =
                e instanceof Error
                    ? e
                    : new Error("Failed to get devices using Voximplant", { cause: e })
            handleError(this.manager, e)
        }
    }
}

function getErrorMessage(manager: InputDeviceManager, e: unknown) {
    const deviceName1 = getDeviceName(manager, 1)
    const deviceName2 = getDeviceName(manager, 2)

    if (e instanceof NoDeviceFoundError) return `${deviceName1} не найден`
    if (!(e instanceof DOMException)) return `Неизвестная ошибка получения доступа к ${deviceName2}`
    switch (e.name) {
        case "AbortError":
            return `Не предоставлен доступ к ${deviceName2}`
        case "NotFoundError":
            return `${deviceName1} не найдена(а)`
        case "NotReadableError":
            return `Нет доступа к ${deviceName2}. Проверьте, что устройство не используется в другим приложением.`
        case "SecurityError":
            return `Доступ к ${deviceName2} запрещён`
        case "NotAllowedError":
            return `Браузеру был запрещён доступ к ${deviceName2}`
        case "TypeError":
            return `Нет доступа к ${deviceName2}. Перейдите на HTTPS-версию сайта`
        default:
            return `Неизвестная ошибка (${e.name}) обращения к ${deviceName2}`
    }
}

function handleError(manager: InputDeviceManager, e: unknown): void {
    const message = getErrorMessage(manager, e)
    toast.error(message)
    SentryWrapper.getInstance().captureException(
        e instanceof DOMException ? e : `Unknown non DOMException: ${e?.toString() ?? "<empty>"}`,
        {}
    )
}

function getDeviceName(manager: InputDeviceManager, variant: number) {
    if (variant === 1) {
        switch (manager) {
            case AudioDeviceManager:
                return "Микрофон"
            case CameraManager:
                return "Камера"
            default:
                return "Неизвестное устройство"
        }
    } else if (variant === 2) {
        switch (manager) {
            case AudioDeviceManager:
                return "микрофону"
            case CameraManager:
                return "камере"
            default:
                return "неизвестному устройству"
        }
    }
    throw "Unknown device name variant"
}

class NoDeviceFoundError extends ErrorWithFallback<unknown> {
    constructor() {
        super("No device found", undefined)
        this.name = this.constructor.name
    }
}
